import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Ambulance, ArrowLeft, CheckCircle2, FileDown } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SignatureBlock from "@/components/SignatureBlock";
import SignatureOnFile from "@/components/SignatureOnFile";
import { getCurrentSignature, uploadSignature } from "@/lib/signatures";
import { FilePreviewProvider, useFilePreview } from "@/context/FilePreviewContext";
import FilePreviewDrawer from "@/components/FilePreviewDrawer";

const SAVE_DEBOUNCE_MS = 1500;

const POSITIONS = ["Driver", "Attendant", "Customer Service", "Dispatcher", "Supervisor", "Trainer", "Other"];
const WORK_STATUSES = ["Full Time (32+ hrs/wk)", "Part Time (under 32 hrs/wk)"];
const EDU_LEVELS = ["Some High School", "High School Diploma / GED", "Some College", "Associate", "Bachelor's", "Master's", "Other"];
const YES_NO = ["Yes", "No"];
const DAYS = [
  ["day_mon", "Mon"], ["day_tue", "Tue"], ["day_wed", "Wed"], ["day_thu", "Thu"],
  ["day_fri", "Fri"], ["day_sat", "Sat"], ["day_sun", "Sun"],
];

function normalizeName(s) {
  return (s || "").trim().replace(/\s+/g, " ").toLowerCase();
}

function underFiveYears(monthStr) {
  if (!monthStr) return false;
  const d = new Date(`${monthStr}-01T00:00:00`);
  if (Number.isNaN(d.getTime())) return false;
  return (Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000) < 5;
}

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function Card({ title, note, children, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-5 mb-4" data-testid={testId}>
      <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 mb-3">{title}</div>
      {note && <div className="text-xs text-slate-500 mb-3 -mt-1">{note}</div>}
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, hint, error, className = "", ...props }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">{label}</label>
      <input
        {...props}
        className={`mt-1 w-full text-sm px-3 py-3 rounded-md border ${error ? "border-rose-300" : "border-slate-200"} disabled:bg-slate-50 disabled:text-slate-600 ${className}`}
        style={{ minHeight: 44 }}
      />
      {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
      {error && <div className="text-[11px] text-rose-600 mt-1">This field is required.</div>}
    </div>
  );
}

function Select({ label, options, placeholder = "Select…", error, ...props }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">{label}</label>
      <select
        {...props}
        className={`mt-1 w-full text-sm px-3 py-3 rounded-md border ${error ? "border-rose-300" : "border-slate-200"} bg-white disabled:bg-slate-50 disabled:text-slate-600`}
        style={{ minHeight: 44 }}
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
      {error && <div className="text-[11px] text-rose-600 mt-1">This field is required.</div>}
    </div>
  );
}

function Check({ label, checked, onChange, disabled }) {
  return (
    <label className="flex items-center gap-2 text-sm" style={{ minHeight: 44 }}>
      <input type="checkbox" checked={!!checked} onChange={onChange} disabled={disabled} style={{ width: 18, height: 18 }} />
      {label}
    </label>
  );
}

export default function DriverApplication() {
  return (
    <FilePreviewProvider>
      <DriverApplicationInner />
    </FilePreviewProvider>
  );
}

function DriverApplicationInner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("return");
  // Only honor a return param that points back into the driver portal, to avoid an open redirect.
  const validReturn = returnTo && returnTo.startsWith("/driver") ? returnTo : null;
  const backTo = validReturn || "/driver";
  const backLabel = validReturn ? "Back to onboarding" : "Back to rides";
  const { openPreview } = useFilePreview();
  const did = user?.driver_id;

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("new"); // new | draft | submitted
  const [data, setData] = useState({});
  const [record, setRecord] = useState(null);
  const [licensePrefill, setLicensePrefill] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [errors, setErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [currentSig, setCurrentSig] = useState(null);
  const [dirtyTick, setDirtyTick] = useState(0);

  const saveTimer = useRef(null);
  const dataRef = useRef(data);
  dataRef.current = data;

  const readOnly = status === "submitted";

  const load = useCallback(async () => {
    if (!did) return;
    setLoading(true);
    try {
      const r = await api.get(`/drivers/${did}/application`);
      const body = r.data;
      setStatus(body.status || "new");
      setData(body.data || {});
      setRecord(body.status === "new" ? null : body);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [did]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!did) return undefined;
    let cancelled = false;
    api.get(`/drivers/${did}/license-prefill`)
      .then((r) => { if (!cancelled) setLicensePrefill(r.data); })
      .catch(() => { if (!cancelled) setLicensePrefill(null); });
    return () => { cancelled = true; };
  }, [did]);

  useEffect(() => {
    if (!did) return;
    getCurrentSignature(did).then(setCurrentSig).catch(() => setCurrentSig(null));
  }, [did]);

  const setField = (key, value) => {
    setData((d) => ({ ...d, [key]: value }));
    setErrors((errs) => errs.filter((e) => e !== key));
    setDirtyTick((n) => n + 1);
  };

  const saveDraft = useCallback(async (silent) => {
    if (!did) return;
    setSaving(true);
    try {
      const r = await api.put(`/drivers/${did}/application/draft`, { data: dataRef.current });
      setStatus(r.data.status);
      setRecord(r.data);
      setSavedAt(Date.now());
      return r.data;
    } catch (e) {
      if (!silent) toast.error(formatApiError(e.response?.data?.detail));
      throw e;
    } finally {
      setSaving(false);
    }
  }, [did]);

  // Debounce autosave: 1.5s after the last change, only once the driver has actually
  // touched something (dirtyTick starts at 0, load() never bumps it).
  useEffect(() => {
    if (dirtyTick === 0 || readOnly) return undefined;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { saveDraft(true).catch(() => {}); }, SAVE_DEBOUNCE_MS);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [dirtyTick, readOnly, saveDraft]);

  const fullLegalName = useMemo(
    () => [data.legal_first, data.legal_last].filter(Boolean).join(" "),
    [data.legal_first, data.legal_last]
  );

  const showPrevAddr = underFiveYears((data.addr_since || "").slice(0, 7));
  const showPrev2Addr = showPrevAddr && underFiveYears((data.prev_since || "").slice(0, 7));

  const handleAdoptSignature = async ({ method, typedName, blob }) => {
    try {
      const sig = await uploadSignature(did, { method, typedName, blob, purpose: "application" });
      setCurrentSig(sig);
      toast.success("Signature saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const doSubmit = async () => {
    setSubmitting(true);
    setErrors([]);
    try {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      await saveDraft(true).catch(() => {});
      const r = await api.post(`/drivers/${did}/application/submit`);
      setStatus("submitted");
      setRecord(r.data);
      setData(r.data.data || dataRef.current);
      toast.success("Application submitted");
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 400 && detail && Array.isArray(detail.missing_fields)) {
        setErrors(detail.missing_fields);
        toast.error("Please fill in the highlighted fields before submitting.");
      } else if (e.response?.status === 409 && detail === "signature_required") {
        toast.error("Please sign before submitting.");
      } else {
        toast.error(formatApiError(detail));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const downloadPdf = () => {
    if (!record?.pdf_doc_id) return;
    openPreview({
      kind: "document",
      id: record.pdf_doc_id,
      meta: { label: "Job application", doc_type: "work_application", driver_id: did },
    });
  };

  const hasErr = (key) => errors.includes(key);
  const licenseIsReadOnly = readOnly || !!licensePrefill;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-[#0A192F] text-white">
        <div className="max-w-lg mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-md bg-[#005E82] flex items-center justify-center"><Ambulance size={18} /></div>
            <div>
              <div className="font-display font-semibold text-sm">Job Application</div>
              <div className="text-[11px] text-slate-400 truncate max-w-[180px]">{user?.name}</div>
            </div>
          </div>
          <button onClick={() => navigate(backTo)} className="min-h-[44px] text-slate-300 text-xs flex items-center gap-1.5 hover:text-white" data-testid="driver-application-back">
            <ArrowLeft size={14} /> {backLabel}
          </button>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 pt-5 pb-24" data-testid="driver-application">
        {loading && <div className="text-sm text-slate-500 text-center py-10">Loading…</div>}

        {!loading && (
          <>
            {!readOnly && (
              <div className="text-[11px] text-slate-500 mb-3 h-4" data-testid="application-save-indicator">
                {saving ? "Saving…" : savedAt ? "Progress saved" : ""}
              </div>
            )}

            {readOnly && (
              <div className="bg-white border border-emerald-200 rounded-md p-6 text-center mb-4" data-testid="application-submitted-banner">
                <CheckCircle2 size={32} className="text-emerald-600 mx-auto mb-3" />
                <div className="font-display font-semibold text-slate-900 text-lg">Application submitted</div>
                <div className="text-sm text-slate-600 mt-1">
                  Submitted on {fmtDate(record?.submitted_at)}. This is a read-only summary of what you sent in.
                </div>
                <button
                  onClick={downloadPdf}
                  disabled={!record?.pdf_doc_id}
                  className="mt-4 min-h-[44px] inline-flex items-center justify-center gap-2 border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-sm rounded-md px-4 py-2.5 disabled:opacity-60"
                  data-testid="download-application-pdf"
                >
                  <FileDown size={14} /> Download your signed application
                </button>
              </div>
            )}

            <Card title="About you" testId="section-about-you">
              <div className="grid grid-cols-2 gap-2">
                <Field label="First name" value={data.legal_first || ""} onChange={(e) => setField("legal_first", e.target.value)} disabled={readOnly} error={hasErr("legal_first")} data-testid="app-legal-first" />
                <Field label="Middle name" value={data.legal_middle || ""} onChange={(e) => setField("legal_middle", e.target.value)} disabled={readOnly || !!data.no_middle} data-testid="app-legal-middle" />
              </div>
              <Check label="I do not have a middle name" checked={data.no_middle} onChange={(e) => setField("no_middle", e.target.checked)} disabled={readOnly} />
              <div className="grid grid-cols-2 gap-2">
                <Field label="Last name" value={data.legal_last || ""} onChange={(e) => setField("legal_last", e.target.value)} disabled={readOnly} error={hasErr("legal_last")} data-testid="app-legal-last" />
                <Field label="Date of birth" type="date" value={data.dob || ""} onChange={(e) => setField("dob", e.target.value)} disabled={readOnly} error={hasErr("dob")} data-testid="app-dob" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Cell phone" type="tel" value={data.cell_phone || ""} onChange={(e) => setField("cell_phone", e.target.value)} disabled={readOnly} error={hasErr("cell_phone")} data-testid="app-cell-phone" />
                <Field label="Other phone" type="tel" value={data.other_phone || ""} onChange={(e) => setField("other_phone", e.target.value)} disabled={readOnly} />
              </div>
            </Card>

            <Card title="Address history" testId="section-address">
              <Field label="Street address" value={data.addr_street || ""} onChange={(e) => setField("addr_street", e.target.value)} disabled={readOnly} error={hasErr("addr_street")} data-testid="app-addr-street" />
              <div className="grid grid-cols-2 gap-2">
                <Field label="City" value={data.addr_city || ""} onChange={(e) => setField("addr_city", e.target.value)} disabled={readOnly} error={hasErr("addr_city")} />
                <Field label="State" value={data.addr_state || ""} onChange={(e) => setField("addr_state", e.target.value)} disabled={readOnly} error={hasErr("addr_state")} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="ZIP" value={data.addr_zip || ""} onChange={(e) => setField("addr_zip", e.target.value)} disabled={readOnly} error={hasErr("addr_zip")} />
                <Field label="At this address since" type="month" value={(data.addr_since || "").slice(0, 7)} onChange={(e) => setField("addr_since", e.target.value)} disabled={readOnly} error={hasErr("addr_since")} data-testid="app-addr-since" />
              </div>

              {showPrevAddr && (
                <div className="border-t border-slate-100 pt-3 mt-1 space-y-3" data-testid="app-prev-addr-block">
                  <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-3 py-2">
                    Regulators require 5 years of address history, so a previous address is required.
                  </div>
                  <Field label="Previous street" value={data.prev_street || ""} onChange={(e) => setField("prev_street", e.target.value)} disabled={readOnly} error={hasErr("prev_street")} />
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="City" value={data.prev_city || ""} onChange={(e) => setField("prev_city", e.target.value)} disabled={readOnly} error={hasErr("prev_city")} />
                    <Field label="State" value={data.prev_state || ""} onChange={(e) => setField("prev_state", e.target.value)} disabled={readOnly} error={hasErr("prev_state")} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="ZIP" value={data.prev_zip || ""} onChange={(e) => setField("prev_zip", e.target.value)} disabled={readOnly} error={hasErr("prev_zip")} />
                    <Field label="At that address since" type="month" value={(data.prev_since || "").slice(0, 7)} onChange={(e) => setField("prev_since", e.target.value)} disabled={readOnly} error={hasErr("prev_since")} />
                  </div>
                </div>
              )}

              {showPrev2Addr && (
                <div className="border-t border-slate-100 pt-3 mt-1 space-y-3" data-testid="app-prev2-addr-block">
                  <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-3 py-2">
                    Your last two addresses still don't cover 5 years, so a third (older) address is required.
                  </div>
                  <Field label="Previous street (older)" value={data.prev2_street || ""} onChange={(e) => setField("prev2_street", e.target.value)} disabled={readOnly} error={hasErr("prev2_street")} />
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="City" value={data.prev2_city || ""} onChange={(e) => setField("prev2_city", e.target.value)} disabled={readOnly} error={hasErr("prev2_city")} />
                    <Field label="State" value={data.prev2_state || ""} onChange={(e) => setField("prev2_state", e.target.value)} disabled={readOnly} error={hasErr("prev2_state")} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="ZIP" value={data.prev2_zip || ""} onChange={(e) => setField("prev2_zip", e.target.value)} disabled={readOnly} error={hasErr("prev2_zip")} />
                    <Field label="At that address since" type="month" value={(data.prev2_since || "").slice(0, 7)} onChange={(e) => setField("prev2_since", e.target.value)} disabled={readOnly} error={hasErr("prev2_since")} />
                  </div>
                </div>
              )}
            </Card>

            <Card title="Position and availability" testId="section-position">
              <Select label="Position applying for" options={POSITIONS} value={data.position || ""} onChange={(e) => setField("position", e.target.value)} disabled={readOnly} error={hasErr("position")} />
              <Field label="Date available" type="date" value={data.date_available || ""} onChange={(e) => setField("date_available", e.target.value)} disabled={readOnly} error={hasErr("date_available")} />
              <Field label="Anticipated wage" value={data.wage || ""} onChange={(e) => setField("wage", e.target.value)} disabled={readOnly} />
              <Select label="Work status" options={WORK_STATUSES} value={data.work_status || ""} onChange={(e) => setField("work_status", e.target.value)} disabled={readOnly} />
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Shifts available</label>
                <div className="flex gap-4 mt-1">
                  <Check label="AM / Morning" checked={data.shift_am} onChange={(e) => setField("shift_am", e.target.checked)} disabled={readOnly} />
                  <Check label="PM / Afternoon" checked={data.shift_pm} onChange={(e) => setField("shift_pm", e.target.checked)} disabled={readOnly} />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Days available</label>
                <div className="flex flex-wrap gap-2 mt-1.5">
                  {DAYS.map(([key, label]) => (
                    <label key={key} className="flex items-center gap-1.5 text-xs border border-slate-200 rounded-md px-2.5" style={{ minHeight: 40 }}>
                      <input type="checkbox" checked={!!data[key]} onChange={(e) => setField(key, e.target.checked)} disabled={readOnly} style={{ width: 16, height: 16 }} />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
            </Card>

            <Card title="Work authorization" testId="section-work-auth">
              <Select label="Legally authorized to work in the USA?" options={YES_NO} value={data.work_authorized || ""} onChange={(e) => setField("work_authorized", e.target.value)} disabled={readOnly} error={hasErr("work_authorized")} />
            </Card>

            <Card title="Licensing" testId="section-licensing">
              {licenseIsReadOnly ? (
                <>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><div className="text-[11px] text-slate-500">License number</div><div className="text-slate-800">{data.license_number || "Not on file"}</div></div>
                    <div><div className="text-[11px] text-slate-500">State</div><div className="text-slate-800">{data.license_state || "Not on file"}</div></div>
                    <div><div className="text-[11px] text-slate-500">Class</div><div className="text-slate-800">{data.license_class || "Not on file"}</div></div>
                    <div><div className="text-[11px] text-slate-500">Expiration</div><div className="text-slate-800">{data.license_expiry ? fmtDate(data.license_expiry) : "Not on file"}</div></div>
                  </div>
                  {!readOnly && (
                    <button onClick={() => navigate("/driver/license")} className="text-xs text-[#005E82] hover:underline" data-testid="app-update-license-link">
                      Update on the license page
                    </button>
                  )}
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="License number" value={data.license_number || ""} onChange={(e) => setField("license_number", e.target.value)} disabled={readOnly} />
                    <Field label="State" value={data.license_state || ""} onChange={(e) => setField("license_state", e.target.value)} disabled={readOnly} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="Class" value={data.license_class || ""} onChange={(e) => setField("license_class", e.target.value)} disabled={readOnly} />
                    <Field label="Expiration" type="date" value={data.license_expiry || ""} onChange={(e) => setField("license_expiry", e.target.value)} disabled={readOnly} />
                  </div>
                </>
              )}
              <Field label="Any restrictions or pending actions? (optional)" value={data.license_restrictions || ""} onChange={(e) => setField("license_restrictions", e.target.value)} disabled={readOnly} />
            </Card>

            <Card title="Health and certifications" testId="section-health">
              <Field label="CPR expiration" type="date" value={data.cpr_expiration || ""} onChange={(e) => setField("cpr_expiration", e.target.value)} disabled={readOnly} />
              <Field label="Last physical" type="date" value={data.last_physical || ""} onChange={(e) => setField("last_physical", e.target.value)} disabled={readOnly} />
              <Field label="Last TB / chest X-ray" type="date" value={data.last_tb || ""} onChange={(e) => setField("last_tb", e.target.value)} disabled={readOnly} />
            </Card>

            <Card title="Education" testId="section-education">
              <Select label="Highest level completed" options={EDU_LEVELS} value={data.edu_level || ""} onChange={(e) => setField("edu_level", e.target.value)} disabled={readOnly} />
              <div className="grid grid-cols-2 gap-2">
                <Field label="School / college" value={data.school || ""} onChange={(e) => setField("school", e.target.value)} disabled={readOnly} />
                <Field label="Year" value={data.grad_year || ""} onChange={(e) => setField("grad_year", e.target.value)} disabled={readOnly} />
              </div>
              <Field label="Degree / area of study" value={data.degree || ""} onChange={(e) => setField("degree", e.target.value)} disabled={readOnly} />
            </Card>

            <Card title="Work history" note="Your most recent employer" testId="section-work-history">
              <Field label="Company name" value={data.wh_company || ""} onChange={(e) => setField("wh_company", e.target.value)} disabled={readOnly} />
              <div className="grid grid-cols-2 gap-2">
                <Field label="From" type="month" value={(data.wh_from || "").slice(0, 7)} onChange={(e) => setField("wh_from", e.target.value)} disabled={readOnly} />
                <Field label="To (leave blank if current)" type="month" value={(data.wh_to || "").slice(0, 7)} onChange={(e) => setField("wh_to", e.target.value)} disabled={readOnly} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="City" value={data.wh_city || ""} onChange={(e) => setField("wh_city", e.target.value)} disabled={readOnly} />
                <Field label="State" value={data.wh_state || ""} onChange={(e) => setField("wh_state", e.target.value)} disabled={readOnly} />
              </div>
              <Field label="Wage" value={data.wh_wage || ""} onChange={(e) => setField("wh_wage", e.target.value)} disabled={readOnly} />
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Job responsibilities</label>
                <textarea
                  value={data.wh_duties || ""}
                  onChange={(e) => setField("wh_duties", e.target.value)}
                  disabled={readOnly}
                  className="mt-1 w-full text-sm px-3 py-3 rounded-md border border-slate-200 disabled:bg-slate-50"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Supervisor" value={data.wh_supervisor || ""} onChange={(e) => setField("wh_supervisor", e.target.value)} disabled={readOnly} />
                <Field label="Supervisor phone" type="tel" value={data.wh_sup_phone || ""} onChange={(e) => setField("wh_sup_phone", e.target.value)} disabled={readOnly} />
              </div>
              <Select label="May we contact them?" options={YES_NO} value={data.wh_may_contact || ""} onChange={(e) => setField("wh_may_contact", e.target.value)} disabled={readOnly} />
              <Field label="Reason for leaving" value={data.wh_reason || ""} onChange={(e) => setField("wh_reason", e.target.value)} disabled={readOnly} />
            </Card>

            <Card title="Prior employment with this company" testId="section-prior-employment">
              <Select label="Ever employed here before?" options={YES_NO} value={data.prior_employed || ""} onChange={(e) => setField("prior_employed", e.target.value)} disabled={readOnly} />
              {data.prior_employed === "Yes" && (
                <Field label="Location and dates" value={data.prior_detail || ""} onChange={(e) => setField("prior_detail", e.target.value)} disabled={readOnly} />
              )}
            </Card>

            <Card title="Convictions" testId="section-convictions">
              <Select label="Ever convicted of a felony or misdemeanor?" options={YES_NO} value={data.convicted || ""} onChange={(e) => setField("convicted", e.target.value)} disabled={readOnly} />
              {data.convicted === "Yes" && (
                <Field label="Basis and date" value={data.conviction_detail || ""} onChange={(e) => setField("conviction_detail", e.target.value)} disabled={readOnly} />
              )}
            </Card>

            <Card title="Referral" testId="section-referral">
              <Field label="How were you referred to us?" value={data.referral || ""} onChange={(e) => setField("referral", e.target.value)} disabled={readOnly} />
            </Card>

            <Card title="Emergency contact" testId="section-emergency">
              <Field label="Name" value={data.emerg_name || ""} onChange={(e) => setField("emerg_name", e.target.value)} disabled={readOnly} error={hasErr("emerg_name")} />
              <div className="grid grid-cols-2 gap-2">
                <Field label="Relationship" value={data.emerg_relationship || ""} onChange={(e) => setField("emerg_relationship", e.target.value)} disabled={readOnly} />
                <Field label="Phone" type="tel" value={data.emerg_phone || ""} onChange={(e) => setField("emerg_phone", e.target.value)} disabled={readOnly} error={hasErr("emerg_phone")} />
              </div>
            </Card>

            <Card title="Consents" testId="section-consents">
              <Check label="I authorize this agency to obtain my motor vehicle (driving) record." checked={data.consent_mvr} onChange={(e) => setField("consent_mvr", e.target.checked)} disabled={readOnly} />
              <Check label="I authorize a DHS background study (NetStudy 2.0)." checked={data.consent_background} onChange={(e) => setField("consent_background", e.target.checked)} disabled={readOnly} />
              <Check label="I consent to pre-employment and ongoing drug & alcohol testing." checked={data.consent_drug} onChange={(e) => setField("consent_drug", e.target.checked)} disabled={readOnly} />
              {(hasErr("consent_mvr") || hasErr("consent_background") || hasErr("consent_drug")) && (
                <div className="text-[11px] text-rose-600">Please check all three authorizations.</div>
              )}
            </Card>

            {!readOnly && (
              <Card title="Signature" testId="section-signature">
                <div className="text-xs text-slate-500">
                  The information I have given is true and accurate to the best of my knowledge.
                </div>
                <Field
                  label="Type your full legal name to sign"
                  value={data.signature_name || ""}
                  onChange={(e) => setField("signature_name", e.target.value)}
                  error={hasErr("signature_name")}
                  data-testid="app-signature-name"
                />
                {data.signature_name && normalizeName(data.signature_name) !== normalizeName(fullLegalName) && (
                  <div className="text-[11px] text-amber-700">Type your name exactly as your first and last name above.</div>
                )}

                {currentSig ? (
                  <SignatureOnFile driverId={did} legalName={fullLegalName} canUpdate />
                ) : (
                  <SignatureBlock legalName={fullLegalName} onAdopt={handleAdoptSignature} />
                )}

                <button
                  onClick={doSubmit}
                  disabled={submitting}
                  className="w-full mt-2 min-h-[44px] flex items-center justify-center gap-2 bg-[#0A192F] hover:bg-[#112240] text-white text-sm rounded-md font-semibold disabled:opacity-60"
                  data-testid="submit-application-btn"
                >
                  {submitting ? "Submitting…" : "Submit application"}
                </button>

                {errors.length > 0 && (
                  <div className="text-xs text-rose-600" data-testid="application-errors">
                    Please complete the highlighted fields above before submitting.
                  </div>
                )}
              </Card>
            )}
          </>
        )}
      </div>
      <FilePreviewDrawer />
    </div>
  );
}
